import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { ConfigService } from '../services/config.service';
import { SKIP_AUTH_REFRESH } from '../http-context-tokens';
import { AUTH_WEB_LOGOUT_PATH, AUTH_WEB_REFRESH_PATH } from './auth-api-paths';

/**
 * Tells the interceptor which calls are “public” auth flows and which UI routes
 * are login/register chrome. Those two ideas stay separate on purpose — see team notes.
 */
@Injectable({ providedIn: 'root' })
export class AuthHttpUrlPolicy {
  constructor(private configService: ConfigService) {}

  /** Login, refresh, forgot password… — never run cookie refresh / session wipe on their 401s. */
  isPublicNoRefreshUrl(url: string): boolean {
    const cfg = this.configService.configIfReady;
    if (!cfg) {
      return false;
    }
    const refreshUrl = `${cfg.BASE_URL.replace(/\/$/, '')}${AUTH_WEB_REFRESH_PATH}`;
    if (url.split('?')[0] === refreshUrl.split('?')[0]) {
      return true;
    }
    const publicPathFragments = [
      '/usermgmt/loginportal',
      '/usermgmt/loginIndividual',
      '/usermgmt/checkForgotCredentials',
      '/usermgmt/forgotCredentials',
      '/usermgmt/checkResetLinkExpiry',
      '/usermgmt/checkResetPassword',
      '/usermgmt/resetPassword',
      '/usermgmt/resetPasswordInternal',
      '/usermgmt/checkLogin',
      '/usermgmt/register',
      '/usermgmt/auth/web/handoff',
      AUTH_WEB_LOGOUT_PATH,
    ];
    return publicPathFragments.some((p) => url.includes(p));
  }

  /** User is already on an auth screen in the SPA — don’t treat odd 401s as “session expired”. */
  isAuthChromeUrl(routerUrl: string): boolean {
    const path = (routerUrl || '').split('#')[0];
    const authPaths = [
      '/login',
      '/change-password',
      '/reset-password',
      '/forgot-password',
      '/forget-password',
      '/register',
      '/individual-registration',
      '/confirmation',
    ];
    if (authPaths.some((p) => path === p || path.startsWith(p + '?'))) {
      return true;
    }
    // Guest sign-link (`/digital-signature?ref=…`) authenticates by ref/OTP, not the
    // cookie session. Session-expiry logout here freezes Sign and blocks /confirmation.
    return /^\/digital-signature\?/.test(path);
  }

  /**
   * TODO(backend): remove when these APIs return EMPTY TOKEN instead of INVALID REQUEST.
   * Temporary: HTTP 200 INVALID REQUEST on these lookups means the access cookie is empty.
   */
  isInvalidRequestRefreshWorkaroundUrl(url: string): boolean {
    const paths = [
      '/orgmgmt/returnNotarization',
      '/orgmgmt/returnDocumentStorage',
      '/orgmgmt/returnOtpAuthentication',
      '/orgmgmt/returnOtpAuthenticationSignLink',
    ];
    return paths.some((p) => url.includes(p));
  }

  /**
   * Same guards for cookie refresh whether triggered by HTTP 401 or by a 200 JSON body
   * (e.g. EMPTY TOKEN after access cookie expired but refresh cookie still valid).
   */
  shouldAttemptSilentRefresh(request: HttpRequest<unknown>): boolean {
    if (request.context.get(SKIP_AUTH_REFRESH)) {
      return false;
    }
    const cfg = this.configService.configIfReady;
    if (!cfg || !request.url.startsWith(cfg.BASE_URL)) {
      return false;
    }
    return !this.isPublicNoRefreshUrl(request.url);
  }

  /** Protected API 401 → try POST /auth/web/refresh once, then retry the same call. */
  shouldAttemptRefresh(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
    routerUrl?: string,
  ): boolean {
    if (error.status !== 401) {
      return false;
    }
    if (routerUrl && this.isAuthChromeUrl(routerUrl)) {
      return false;
    }
    return this.shouldAttemptSilentRefresh(request);
  }
}
